import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosMedical } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";
import { resolveMedicalScope } from "./medicalScope";
import Cookies from "js-cookie";

const medicalScopeConfig = (state, target, headers = {}) => {
  const { userId, headers: scopeHeaders } = resolveMedicalScope(state, target);
  const isDoctorPage =
    typeof window !== "undefined" &&
    /(?:^|\/)(?:doctorPages|doctor|medecin)(?:\/|$)/i.test(
      window.location.pathname
    );
  const doctorToken = isDoctorPage
    ? Cookies.get("doctorAccessToken") ||
      Cookies.get("doctorToken") ||
      localStorage.getItem("doctorAccessToken") ||
      localStorage.getItem("doctorToken")
    : null;
  const scopedHeaders = doctorToken
    ? { ...headers, Authorization: `Bearer ${doctorToken}` }
    : headers;

  return {
    userId,
    headers: { ...scopedHeaders, ...scopeHeaders },
  };
};

/* 🔎 util debug amélioré */
const logReq = (tag, obj) => {
  console.group(`🔍 [thunk:${tag}]`);
  try {
  } catch {
  }
  console.groupEnd();
};

/** ➕ ADD (POST JSON) */
export const createMedicalInfo = createAsyncThunk(
  "medical/createMedicalInfo",
  async ({ userId, values }, { rejectWithValue, getState }) => {
    try {
      const payload = { ...values };
      const patientId = userId;

      console.group("🚀 CREATE MEDICAL INFO - REQUEST");
      console.groupEnd();

      const { data } = await axiosMedical.post(
        ENDPOINTS.MEDICAL.CREATE(patientId),
        payload,
        medicalScopeConfig(getState(), patientId, { "Content-Type": "application/json" })
      );

      console.group("✅ CREATE MEDICAL INFO - RESPONSE");
      console.groupEnd();

      return data?.data ?? data;
    } catch (err) {
      const detail = err?.response?.data || err.message || "Erreur réseau";
      return rejectWithValue(detail);
    }
  }
);

/** 🔁 UPDATE (JSON ou multipart) */
export const updateMedicalInfo = createAsyncThunk(
  "medical/updateMedicalInfo",
  async ({ userId, values, avatarFile }, { rejectWithValue, getState }) => {
    try {
      const patientId = userId;

      if (!avatarFile) {
        const { data } = await axiosMedical.put(
          ENDPOINTS.MEDICAL.UPDATE(patientId),
          values,
          medicalScopeConfig(getState(), patientId, { "Content-Type": "application/json" })
        );
        return data?.data ?? data;
      }

      const fd = new FormData();
      Object.entries(values || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") {
          fd.append(k, typeof v === "object" ? JSON.stringify(v) : String(v));
        }
      });
      fd.append("avatar", avatarFile);

      const { data } = await axiosMedical.put(
        ENDPOINTS.MEDICAL.UPDATE(patientId),
        fd,
        medicalScopeConfig(getState(), patientId, { "Content-Type": "multipart/form-data" })
      );

      return data?.data ?? data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

/** 🔍 GET - Dernier snapshot */
export const fetchMedicalInfoByUserId = createAsyncThunk(
  "medical/fetchMedicalInfoByUserId",
    async (target, { rejectWithValue, getState }) => {
    try {
      const { userId: patientId } = medicalScopeConfig(getState(), target);
      const { data } = await axiosMedical.get(
        ENDPOINTS.MEDICAL.BY_USER(patientId),
        medicalScopeConfig(getState(), patientId)
      );
      return data?.data ?? data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

/** � GET - User insurance list (extracted from medical record) */
export const fetchUserInsurances = createAsyncThunk(
  "medical/fetchUserInsurances",
  async (userId, { rejectWithValue, getState }) => {
    try {
      const patientId = userId;
      const { data } = await axiosMedical.get(
        ENDPOINTS.MEDICAL.BY_USER(patientId),
        medicalScopeConfig(getState(), patientId)
      );
      const insurance = Array.isArray(data?.insurance) ? data.insurance : [];
      return insurance;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

/** �📋 GET - Historique personnel (timeline) */
export const fetchMedicalHistory = createAsyncThunk(
  "medical/fetchMedicalHistory",
  async (userId, { rejectWithValue, getState }) => {
    try {
      const { userId: scopedUserId, headers } = medicalScopeConfig(
        getState(),
        userId
      );
      const url = ENDPOINTS.MEDICAL.PERSONAL_HISTORY_TIMELINE(scopedUserId);
      const { data } = await axiosMedical.get(url, { headers });

      console.group("✅ FETCH PERSONAL TIMELINE - RESPONSE");
      console.groupEnd();

      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

/** 🔍 GET - Par ID */
export const fetchMedicalInfoById = createAsyncThunk(
  "medical/fetchMedicalInfoById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosMedical.get(ENDPOINTS.MEDICAL.BY_ID(id));
      return data?.data ?? data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

/** 🔁 UPDATE - Par ID */
export const updateMedicalInfoById = createAsyncThunk(
  "medical/updateMedicalInfoById",
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const { data } = await axiosMedical.put(
        ENDPOINTS.MEDICAL.UPDATE_BY_ID(id),
        values,
        { headers: { "Content-Type": "application/json" } }
      );
      return data?.data ?? data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

/** 🗑️ DELETE - Par ID */
export const deleteMedicalInfoById = createAsyncThunk(
  "medical/deleteMedicalInfoById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosMedical.delete(
        ENDPOINTS.MEDICAL.DELETE_BY_ID(id)
      );
      return { id, ...data };
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

/** 🗑️ DELETE Snapshot complet */
export const deleteAllMedicalInfo = createAsyncThunk(
  "medical/deleteAllMedicalInfo",
  async (userId, { rejectWithValue }) => {
    try {
      const { data } = await axiosMedical.delete(
        ENDPOINTS.MEDICAL.DELETE(userId)
      );
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

/** 🧾 GET – Insurance providers */
export const fetchInsuranceProviders = createAsyncThunk(
  "medical/fetchInsuranceProviders",
  async (_void, { rejectWithValue }) => {
    try {
      const { data } = await axiosMedical.get(
        ENDPOINTS.MEDICAL.INSURANCE_PROVIDERS,
        { headers: { Accept: "application/json" } }
      );
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);
